"use client";

import {JSX, useEffect, useState} from "react";

import {useKeenSlider} from "keen-slider/react";

import {SpaceProps, useSectionSpace} from "@/sections/use-section-space";

import type {FeatureCardProps} from "@/shared/ui/data-display/cards";
import {FeatureCard} from "@/shared/ui/data-display/cards";
import {ContainerBase} from "@/shared/ui/layout/container-base";

import "./our-advantages.scss";
import "keen-slider/keen-slider.min.css";

export interface AdvantagesProps {
    property: Array<FeatureCardProps>;
    space?: SpaceProps;
    anchorId?: string;
}

export function Advantages({property, space, anchorId}: AdvantagesProps): JSX.Element | null {
    const sectionVars = useSectionSpace(space);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = (): void => {
            setIsMobile(window.innerWidth < 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);

        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const [sliderRef, instanceRef] = useKeenSlider({
        slides: {
            perView: 2,
            spacing: 16,
        },
        breakpoints: {
            "(min-width: 768px)": {
                slides: {
                    perView: 3,
                    spacing: 15,
                },
            },
        },
        loop: property.length > 2,
        drag: true,
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    if (!property?.length) return null;

    // Show dots when there are more items than can be displayed at once
    // Mobile shows 2 items, desktop shows 3 items
    const shouldShowDots = isMobile ? property.length > 2 : property.length > 3;

    return (
        <section className="our-advantages" style={sectionVars} id={anchorId}>
            <ContainerBase>
                <div ref={sliderRef} className="keen-slider our-advantages__wrap">
                    {property.map((card) => (
                        <div key={card.id} className="keen-slider__slide our-advantages__slide">
                            <FeatureCard {...card} />
                        </div>
                    ))}
                </div>
                {loaded && instanceRef.current && shouldShowDots && (
                    <div className="our-advantages__dots">
                        {[...Array(instanceRef.current.track.details.slides.length).keys()].map((idx) => {
                            return (
                                <button
                                    key={idx}
                                    onClick={() => {
                                        instanceRef.current?.moveToIdx(idx);
                                    }}
                                    className={"our-advantages__dot" + (currentSlide === idx ? " our-advantages__dot--active" : "")}
                                />
                            );
                        })}
                    </div>
                )}
            </ContainerBase>
        </section>
    );
}
